
import { useState, useEffect, useCallback } from "react"
import { supabase } from "@/integrations/supabase/client"
import { logger } from "@/lib/services/logger-service"
import { ChildDropdownItem } from "./types"

export const useChildrenData = (providedChildren?: ChildDropdownItem[]) => {
  const [loadedChildren, setLoadedChildren] = useState<ChildDropdownItem[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingError, setLoadingError] = useState<string | null>(null)
  
  const fetchChildren = useCallback(async () => {
    try {
      setLoading(true)
      setLoadingError(null)
      
      // Get current user/caregiver
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        throw new Error('Not authenticated')
      }
      
      // Get caregiver ID first
      const { data: caregiverData, error: caregiverError } = await supabase
        .from('caregivers')
        .select('id')
        .eq('auth_user_id', user.id)
        .maybeSingle()
      
      if (caregiverError || !caregiverData) {
        throw new Error('Caregiver not found')
      }
      
      // Now query children with caregiver_id filter - only select essential fields
      const { data, error } = await supabase
        .from('children')
        .select('id, name, date_of_birth')
        .eq('caregiver_id', caregiverData.id)
        .order('name')
      
      if (error) throw error
      
      logger.debug('Fetched children for caregiver', {
        component: 'ChildSelector',
        caregiverId: caregiverData.id,
        childCount: data?.length || 0
      })
      
      // Map to our simplified type
      const childrenData: ChildDropdownItem[] = data?.map(child => ({
        id: child.id,
        name: child.name,
        date_of_birth: child.date_of_birth
      })) || []
      
      setLoadedChildren(childrenData)
      return childrenData
    } catch (error) {
      console.error('Error fetching children:', error)
      setLoadingError(error instanceof Error ? error.message : 'Failed to load children')
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loadedChildren,
    loading,
    loadingError,
    fetchChildren
  }
}
